import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { ChevronRight, Users, Target, TrendingUp, Heart, Award, Globe, ArrowRight, Activity, Shield, Trophy, BookOpen, MapPin, Star, Zap, Play } from "lucide-react";

function KayungaConnect() {
  const [isVisible, setIsVisible] = useState({});
  const [activeProgram, setActiveProgram] = useState('leadership');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const stats = [
    { icon: Users, number: "100%", label: "Community Coverage", description: "Connecting all Kayunga areas", color: "from-blue-500 to-cyan-500" },
    { icon: Trophy, number: "∞", label: "Volunteer Network", description: "Unlimited community leadership potential", color: "from-green-500 to-emerald-500" },
    { icon: Activity, number: "Multi", label: "Sport Disciplines", description: "Games and sports for all ages", color: "from-purple-500 to-pink-500" },
    { icon: Heart, number: "24/7", label: "Community Impact", description: "Continuous volunteer activities", color: "from-orange-500 to-red-500" }
  ];

  const programs = [
    {
      id: 'leadership',
      icon: Users,
      title: "Leadership Development",
      description: "Cultivating community leaders who inspire and organize volunteer activities across Kayunga",
      impact: "Sustainable Leadership",
      color: "from-blue-500 to-cyan-500",
      details: "Our leadership development program identifies and nurtures natural community leaders, providing them with the skills and networks needed to create lasting positive change through volunteerism."
    },
    {
      id: 'sports',
      icon: Trophy,
      title: "Sports & Games Programs",
      description: "Using sports as a catalyst for community building, youth engagement, and social cohesion",
      impact: "Unity Through Play",
      color: "from-green-500 to-emerald-500",
      details: "Sports and games serve as universal languages that bring communities together, breaking down barriers and creating shared experiences that foster cooperation and mutual understanding."
    },
    {
      id: 'network',
      icon: Globe,
      title: "Community Networking",
      description: "Building interconnected volunteer networks that amplify impact across all Kayunga areas",
      impact: "Collective Power",
      color: "from-purple-500 to-pink-500",
      details: "By connecting volunteers across different areas and interests, we create a powerful network effect where individual efforts multiply into community-wide transformation."
    },
    {
      id: 'volunteer',
      icon: Heart,
      title: "Volunteer Coordination",
      description: "Systematically organizing and supporting volunteer activities that address community needs",
      impact: "Organized Impact",
      color: "from-orange-500 to-red-500",
      details: "Strategic volunteer coordination ensures that community efforts are focused, efficient, and sustainable, maximizing the positive impact of every volunteer hour contributed."
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Youth Engagement",
      description: "Meaningful activities that channel young energy into positive community action"
    },
    {
      icon: Shield,
      title: "Social Cohesion",
      description: "Building stronger bonds between different community groups and generations"
    },
    {
      icon: Target,
      title: "Skill Development",
      description: "Teaching leadership, teamwork, and organizational skills through sports and volunteerism"
    },
    {
      icon: Star,
      title: "Community Pride",
      description: "Fostering local ownership and pride in community development initiatives"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <Header />
      
      {/* Hero Image Section */}
      {/* Hero Image Section */}
      <div className="relative pt-24 sm:pt-28">
        <img 
          src="img/connect.jpg" 
          className="w-full h-64 md:h-96 lg:h-[500px] object-cover"
          alt="Kayunga Connect Community Sports"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        
        {/* Hero Content Overlay */}
        {/* Hero Content Overlay */}
         <div className="absolute inset-0 flex items-center justify-center mt-16 sm:mt-20">
          <div className="text-center text-white px-4">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Activity className="w-4 h-4 mr-2" />
              Community Sports & Leadership
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-blue-400 via-green-400 to-cyan-400 bg-clip-text text-transparent">
                Kayunga Connect
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Connecting communities through sports, building leaders through action
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="mission-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['mission-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                Our <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Mission</span>
              </h2>
              <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-green-500 rounded-full mx-auto mb-8"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="bg-gradient-to-br from-blue-600 via-green-600 to-cyan-600 rounded-3xl p-1 shadow-2xl">
                  <div className="bg-white rounded-3xl p-8 md:p-12">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-green-500 rounded-2xl flex items-center justify-center mr-4">
                        <Globe className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800">Community Network Building</h3>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Kayunga Connect creates a comprehensive network of passionate individuals who 
                      lead and inspire volunteer activities throughout the Kayunga region. Through 
                      sports, games, and organized community action, we build bridges between people 
                      and unlock the collective power of community engagement.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-blue-500 transform hover:scale-105 transition-all duration-300">
                    <h4 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                      <Activity className="w-6 h-6 text-blue-600 mr-3" />
                      Sports as Catalyst
                    </h4>
                    <p className="text-gray-600">
                      Using games and sports as universal languages that unite diverse community members, 
                      creating shared experiences and natural leadership opportunities.
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-green-500 transform hover:scale-105 transition-all duration-300">
                    <h4 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                      <Users className="w-6 h-6 text-green-600 mr-3" />
                      Leadership Development
                    </h4>
                    <p className="text-gray-600">
                      Identifying and nurturing community leaders who can inspire others to volunteer 
                      and contribute to positive change in their neighborhoods.
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-cyan-500 transform hover:scale-105 transition-all duration-300">
                    <h4 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                      <Heart className="w-6 h-6 text-cyan-600 mr-3" />
                      Sustainable Volunteerism
                    </h4>
                    <p className="text-gray-600">
                      Building systems and networks that ensure volunteer activities continue and grow, 
                      creating lasting positive impact across all Kayunga communities.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="stats-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['stats-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Network <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Impact</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Measuring the reach and effectiveness of our community connection initiatives
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-green-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  id={`stat-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`stat-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-blue-500">
                    <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-gray-800 mb-2">{stat.number}</div>
                      <div className="text-lg font-semibold text-blue-600 mb-2">{stat.label}</div>
                      <div className="text-sm text-gray-600">{stat.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Programs Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Program <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Components</span>
            </h2>
            <p className="text-xl text-gray-600">Comprehensive approach to community connection and leadership</p>
          </div>

          {/* Program Navigation */}
          <div className="flex flex-wrap justify-center mb-12">
            {programs.map((program) => {
              const Icon = program.icon;
              return (
                <button
                  key={program.id}
                  onClick={() => setActiveProgram(program.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-lg transition-all duration-300 mx-2 mb-2 ${
                    activeProgram === program.id 
                      ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white shadow-lg transform scale-105' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {program.title}
                </button>
              );
            })}
          </div>

          {/* Active Program Content */}
          <div className="transition-all duration-500">
            {programs.map((program) => {
              const Icon = program.icon;
              return (
                activeProgram === program.id && (
                  <div key={program.id} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                      <div className={`w-20 h-20 bg-gradient-to-r ${program.color} rounded-3xl flex items-center justify-center mb-6`}>
                        <Icon className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-800 mb-6">{program.title}</h3>
                      <p className="text-xl text-gray-600 mb-6">{program.description}</p>
                      <p className="text-lg text-gray-700 leading-relaxed mb-6">{program.details}</p>
                      <div className={`inline-flex items-center bg-gradient-to-r ${program.color} bg-opacity-10 px-4 py-2 rounded-full`}>
                        <div className={`w-3 h-3 bg-gradient-to-r ${program.color} rounded-full mr-3`}></div>
                        <span className="font-semibold text-gray-800">{program.impact}</span>
                      </div>
                    </div>
                    <div className="relative">
                      <div className={`bg-gradient-to-br ${program.color} rounded-3xl p-1 shadow-2xl`}>
                        <img
                          src="img/connect.jpg"
                          alt={program.title}
                          className="w-full h-80 object-cover rounded-3xl"
                        />
                      </div>
                    </div>
                  </div>
                )
              );
            })}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Community <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-gray-600">The positive outcomes of connected community action</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-green-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 via-green-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <Play className="w-5 h-5 mr-3" />
              Connect • Lead • Transform
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Join the Network
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Be part of a growing network of community leaders who are using sports and volunteerism 
              to create positive change across Kayunga. Together, we can build stronger, more connected communities.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button 
                onClick={() => window.location.href = '/contact-us'}
                className="bg-white text-blue-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get Connected
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default KayungaConnect;